import { useEffect, useState } from 'react';
import { useMyAccountStore, type MyAccountTab } from '../../stores/useMyAccountStore';
import { getSessionUser, getIsImpersonating } from '../../utils/session';
import { MyAccountNav } from './components/MyAccountNav';
import { SecurityInfoSection } from './components/SecurityInfoSection';
import { NotificationsSection } from './components/NotificationsSection';

export interface MyAccountMountProps {
  activeTab: MyAccountTab | null;
  config: {
    customerServicePhone: string;
    customerServiceEmail: string;
    clientName: string;
  };
  navigate: {
    toFeatures: () => void;
  };
}

export function MyAccountPage({ activeTab: initialTab, config }: MyAccountMountProps) {
  const {
    activeTab,
    pendingTabChange,
    gv,
    setActiveTab,
    loadFromSession,
    fetchGuidedValue,
    confirmTabChange,
    cancelTabChange,
  } = useMyAccountStore();

  const [isUsingSSO, setIsUsingSSO] = useState(false);
  const [isImpersonating] = useState(() => getIsImpersonating());
  const [gvFetched, setGvFetched] = useState(false);

  // Load session user on mount and set initial tab
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional run-once on mount
  useEffect(() => {
    const sessionUser = getSessionUser();
    if (sessionUser) {
      loadFromSession(sessionUser);
      setIsUsingSSO(sessionUser.IsUsingSSO === true);
    }
    setActiveTab(initialTab);
  }, []);

  // Fetch GV data once when the notifications tab is first shown
  useEffect(() => {
    if (activeTab !== 'notifications' || gvFetched) return;

    const sessionUser = getSessionUser();
    if (!sessionUser) return;

    const lakerSubscriberNum = sessionUser.LakerSubscriberNum as string | undefined;
    const lakerPersonCode = sessionUser.LakerPersonCode as string | undefined;

    if (lakerSubscriberNum && lakerPersonCode) {
      const memberId = `${lakerSubscriberNum}*${lakerPersonCode}`;
      fetchGuidedValue(memberId);
    }

    setGvFetched(true);
  }, [activeTab, gvFetched, fetchGuidedValue]);

  return (
    <div className="my-account-container">
      <div className="row">
        <div className="col-md-4 col-sm-12" style={{ borderRight: '1px solid #000', paddingRight: 0 }}>
          <MyAccountNav isUsingSSO={isUsingSSO} />
        </div>

        <div className="col-md-8 col-sm-12">
          {activeTab === null && (
            <div className="account-wrapper">
              <div className="default-account-tab">
                <h4>Select an option from the list to update your account settings.</h4>
              </div>
            </div>
          )}

          {activeTab === 'security' && !isUsingSSO && (
            <SecurityInfoSection
              isImpersonating={isImpersonating}
              customerServicePhone={config.customerServicePhone}
            />
          )}

          {activeTab === 'notifications' && (
            <NotificationsSection
              isImpersonating={isImpersonating}
              clientName={config.clientName}
              customerServicePhone={config.customerServicePhone}
            />
          )}

          {/* SSO users land on notifications; security tab not shown */}
          {activeTab === 'security' && isUsingSSO && (
            <NotificationsSection
              isImpersonating={isImpersonating}
              clientName={config.clientName}
              customerServicePhone={config.customerServicePhone}
            />
          )}
        </div>
      </div>

      {/* Unsaved-changes confirmation dialog. Matches production's actual dialog exactly:
          prod renders this via the legacy alertify.confirm() call, styled by
          nav.alertify-buttons / .alertify-button-ok / #alertify-cancel in style.css + the
          brand overrides in style-maxorplus.css (centered title/body, centered pill
          buttons in the app's brand orange/yellow, and a 5px brand-orange bar under the
          button row). Replicated inline here — same markup as RefillsPage's sidebar
          dialog — rather than calling the global alertify library, so it stays a
          controlled, testable React component. */}
      {pendingTabChange && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            height: '100%',
            width: '100%',
            background: 'rgba(0, 0, 0, 0.7)',
            zIndex: 1040,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="unsaved-dialog-title"
          onKeyDown={e => {
            if (e.key === 'Escape') {
              cancelTabChange();
            }
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: 8,
              boxShadow: '0 3px 3px rgba(0, 0, 0, .3)',
              padding: 25,
              width: 550,
              maxWidth: '90%',
              textAlign: 'center',
            }}
          >
            <h3 id="unsaved-dialog-title" style={{ textTransform: 'uppercase', fontWeight: 'bold', marginTop: 0 }}>
              Unsaved Changes
            </h3>
            <p style={{ marginBottom: 15 }}>
              The form contains unsaved changes, are you sure you want to leave this screen?
            </p>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 10,
                paddingBottom: 15,
                borderBottom: '5px solid #FF471A',
              }}
            >
              <button
                type="button"
                style={{
                  backgroundColor: '#FF471A',
                  color: '#FAFAFA',
                  border: 'none',
                  borderRadius: 4,
                  padding: '8px 20px',
                  cursor: 'pointer',
                }}
                onClick={cancelTabChange}
              >
                Cancel
              </button>
              <button
                type="button"
                style={{
                  backgroundColor: '#FFD24C',
                  color: '#1E1E1E',
                  border: 'none',
                  borderRadius: 4,
                  padding: '8px 20px',
                  minWidth: 75,
                  cursor: 'pointer',
                }}
                onClick={confirmTabChange}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* GV loading overlay — shown when GV is updating after notification save */}
      {gv.loadingGv && gv.isUsingGv && (
        <div className="gv-spinner">
          <i className="fa fa-spinner fa-spin" style={{ fontSize: 24 }} aria-hidden="true" />{' '}
          Updating Preferences...
        </div>
      )}
    </div>
  );
}
